import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import {
  BanknoteIcon,
  ClipboardListIcon,
  DownloadIcon,
  RotateCcwIcon,
  XCircleIcon,
} from "lucide-react";
import { useCallback } from "react";
import {
  useRefundReportReasonsQuery,
  useRefundReportSummaryQuery,
} from "../api/queries";
import {
  RefundReportFilter,
  type RefundReportSearch,
} from "../ui/RefundReportFilter";
import { RefundReportReasonsTable } from "../ui/RefundReportReasonsTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function getDefaultFilters(): FilterConditionDTO[] {
  return [
    {
      field: "requestedAt",
      op: "and",
      value: [
        {
          field: "requestedAt",
          op: "gte",
          value: `${format(subDays(new Date(), 30), "yyyy-MM-dd")} 00:00:00`,
        },
        {
          field: "requestedAt",
          op: "lte",
          value: `${format(new Date(), "yyyy-MM-dd")} 23:59:59`,
        },
      ],
    } as FilterConditionDTO,
  ];
}

export function RefundReportPage() {
  const search = useSearch({
    from: "/app/reports/refund",
  }) as RefundReportSearch;

  const filters =
    search.filters && search.filters.length > 0
      ? search.filters
      : getDefaultFilters();
  const query = { filters };

  const { data: summary, isLoading: summaryLoading } =
    useRefundReportSummaryQuery(query);
  const { data: reasons = [], isLoading: reasonsLoading } =
    useRefundReportReasonsQuery(query);

  const exportCsv = useCallback(() => {
    const header = "ເຫດຜົນ,ຈຳນວນຄັ້ງ";
    const rows = reasons.map(
      (r) => `${escapeCsvCell(r.reason)},${escapeCsvCell(r.count)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refund-report-reasons-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [reasons]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານການຄືນເງິນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານການຄືນເງິນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງສະຖານະ ຍອດເງິນ ແລະເຫດຜົນທີ່ພົບເລື້ອຍ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={reasons.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <RefundReportFilter />
        </div>

        <ReportSummaryStatGrid columns={5}>
          <ReportSummaryStatCard
            title="ຈຳນວນຄຳຂໍທັງໝົດ"
            icon={ClipboardListIcon}
            tone="attendance"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.totalCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ຍອດທີ່ຂໍຄືນ"
            icon={BanknoteIcon}
            tone="refunds"
            isLoading={summaryLoading}
            amountKip={summary?.totalRefundAmountRequested ?? 0}
          />
          <ReportSummaryStatCard
            title="ຍອດທີ່ຄືນແລ້ວ"
            icon={RotateCcwIcon}
            tone="revenue"
            isLoading={summaryLoading}
            amountKip={summary?.totalRefundAmountCompleted ?? 0}
          />
          <ReportSummaryStatCard
            title="ລໍຖ້າກວດສອບ"
            icon={ClipboardListIcon}
            tone="packages"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.requestedCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ຄືນເງິນແລ້ວ"
            icon={XCircleIcon}
            tone="students"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.refundedCount ?? 0)}
          />
        </ReportSummaryStatGrid>

        <Card>
          <CardHeader>
            <CardTitle>ເຫດຜົນທີ່ພົບເລື້ອຍ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ຈຳນວນຄັ້ງທີ່ເຫດຜົນແຕ່ລະອັນຖືກລະບຸໃນຄຳຂໍຄືນເງິນ
            </p>
          </CardHeader>
          <CardContent>
            <RefundReportReasonsTable
              isLoading={reasonsLoading}
              data={reasons}
            />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
